/**
 * Returns a data URL of the image with a logo overlay.
 * @param {string} imageUrl - The URL of the image to watermark.
 * @param {string} logoUrl - The URL of the logo image (PNG with transparency).
 * @param {Object} options - Optional settings.
 * @returns {Promise<string>} - Resolves to a data URL.
 */
export const getWatermarkedImageUrl = (
  imageUrl,
  logoUrl = '/logo.png',
  options = {}
) => {
  return new Promise((resolve, reject) => {
    const {
      logoWidth = 150,         // desired logo width in pixels (maintains aspect ratio)
      position = 'bottom-right',
      padding = 20,
      opacity = 0.8,           // 0.0 - 1.0
      backgroundColor = 'rgba(0,0,0,0.3)',
    } = options;

    // Load the original image
    fetch(imageUrl, { mode: 'cors' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch image');
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          // Create canvas with original dimensions
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;

          // Draw original image
          ctx.drawImage(img, 0, 0);

          // Now load the logo
          const logo = new Image();
          logo.crossOrigin = 'anonymous';
          logo.onload = () => {
            // Calculate logo dimensions (maintain aspect ratio)
            const aspect = logo.width / logo.height;
            let w = logoWidth;
            let h = w / aspect;
            // If logo is larger than image, scale down to fit
            if (w > canvas.width * 0.5) w = canvas.width * 0.5;
            if (h > canvas.height * 0.5) h = canvas.height * 0.5;
            // Recalculate if still too big
            if (w > canvas.width * 0.4) w = canvas.width * 0.4;
            if (h > canvas.height * 0.4) h = canvas.height * 0.4;

            let x, y;
            const pad = padding;
            switch (position) {
              case 'bottom-right':
                x = canvas.width - w - pad;
                y = canvas.height - h - pad;
                break;
              case 'bottom-left':
                x = pad;
                y = canvas.height - h - pad;
                break;
              case 'top-right':
                x = canvas.width - w - pad;
                y = pad;
                break;
              case 'top-left':
                x = pad;
                y = pad;
                break;
              case 'center':
                x = (canvas.width - w) / 2;
                y = (canvas.height - h) / 2;
                break;
              default:
                x = canvas.width - w - pad;
                y = canvas.height - h - pad;
            }

            // Optional background rectangle behind logo
            if (backgroundColor) {
              ctx.fillStyle = backgroundColor;
              ctx.fillRect(x - 10, y - 10, w + 20, h + 20);
            }

            // Draw logo with opacity
            ctx.globalAlpha = opacity;
            ctx.drawImage(logo, x, y, w, h);
            ctx.globalAlpha = 1.0;

            const dataUrl = canvas.toDataURL('image/png');
            resolve(dataUrl);
          };
          logo.onerror = () => {
            // If logo fails to load, fallback to original image
            resolve(URL.createObjectURL(blob));
          };
          logo.src = logoUrl;
        };
        img.onerror = () => reject(new Error('Image load error'));
        img.src = url;
      })
      .catch(reject);
  });
};